import Button from '@blocks/Button'
import CurrentSalon from '@blocks/CurrentSalon'
import Footer, { FooterContacts, FooterDevelopedBy } from '@blocks/Footer'
import Grid from '@blocks/Grid'
import { Background, Container } from '@styles/helpers'
import { route } from '@helpers'
import useGet from '@hooks/useGet'
import { desktop, mTablet, tablet } from '@styles/mixins'
import { colors, typography } from '@styles/vars'
import React from 'react'
import styled, { css } from 'styled-components'
import Feature from './components/Feature'
import LandingHeader from './components/Header'
import MainScreenBg from './images/mainscreenbg.jpg'

export default function LandingPage() {
    return (
        <>
            <MainScreen />
            <Features />
            <Salons />
            <Footer column1={<FooterTryIt />} column2={<FooterDevelopedBy />} column3={<FooterContacts />} />
        </>
    )
}

// SECTIONS
const MainScreen = () => {
    return (
        <MainScreenWrapper>
            <HeaderWrapper>
                <LandingHeader />
            </HeaderWrapper>
            <ContentWrapper>
                <Grid gap="20px|30px">
                    <Grid gap="10px">
                        <Heading>International Salons</Heading>
                        <p>Participate in worldwide photo contests patronaged by FIAP & PSA</p>
                    </Grid>
                    <Button to={route("signup")}>Sign up</Button>
                </Grid>
            </ContentWrapper>
            <BackgroundWrapper>
                <Background image={MainScreenBg} />
            </BackgroundWrapper>
        </MainScreenWrapper>
    )
}

const Features = () => {
    return (
        <FeatureWrapper id="features">
            <Grid gap="30px">
                <Heading2>Features</Heading2>
                <Grid columns="3" gap="45px|60px">
                    <Feature
                        title="All salons in one place" 
                        description="Don’t register multiple times to different salons. Use one account to participate in multiple contests." 
                        to={route("signup")}
                        cta="Sign up" />
                    <Feature
                        title="Store your information" 
                        description="Use our service to store your photos and use them in new salons without reuploading!" 
                        to={route("signup")}
                        cta="Sign up" />
                    <Feature
                        title="Manage your group easier" 
                        description="Additional tools to participate in salons as a group. Manage the group and get discounts for its members." 
                        to={route("signup")}
                        cta="Sign up" />
                </Grid>
            </Grid>
        </FeatureWrapper>
    )
}

const Salons = () => {
    const { data: salons } = useGet<Resources.CurrentSalon[]>("/api/salons")

    if (!salons) return null

    return (
        <div id="salons">
            {salons.map((salon, idx) => <CurrentSalon key={salon.id} data={salon} idx={idx} />)}
        </div>
    )
}

// COMPONENTS
const FooterTryIt = () => {
    return (
        <Grid gap="20px">
            <h3>Try it now</h3>
            <Button to={route("signup")} scheme="reversed" width="fixed">Sign up</Button>
        </Grid>
    )
}

// STYLES
const MainScreenWrapper = styled.div`
    display: flex;
    flex-direction: column;

    ${desktop(css`
        height: 100vh;
        flex-direction: row;
    `)}
`

const HeaderWrapper = styled(Container)`
    height: 100px;
    width: 100%;
    background: ${colors.white};

    ${desktop(css`
        position: absolute;
        background: transparent;
        top: 0;
        left: 0;
    `)}
`

const BackgroundWrapper = styled.div`
    z-index: -1;
    width: 100%;
    height: calc((100vh - 100px) / 2);

    ${desktop(css`
        height: 100%;
    `)}
`

const ContentWrapper = styled(Container)`
    display: flex;
    align-items: center;
    height: calc((100vh - 100px) / 2);
    padding-bottom: 30px;

    ${mTablet(css`
        max-width: 600px;

        p {
            max-width: 350px;
        }

        a {
            max-width: 200px;
        }
    `)}

    ${desktop(css`
        height: 100%;
        display: flex;
        align-items: center;
        max-width: 465px;
    `)}
`

const Heading = styled.h1`
    font-size: 13vw;
    margin-bottom: 0;
    line-height: 100%;
    letter-spacing: -2px;

    ${tablet(css`
        font-size: ${typography.sizes.h2};
    `)}

    ${desktop(css`
        font-size: ${typography.sizes.h1};
    `)}
`

const Heading2 = styled.h2`
    font-size: 11vw;
    margin-bottom: 0;
    line-height: 100%;
    letter-spacing: -2px;

    ${tablet(css`
        font-size: ${typography.sizes.h2};
    `)}
`

const FeatureWrapper = styled(Container)`
    padding-top: 45px;
    padding-bottom: 60px;

    ${mTablet(css`
        padding-top: 90px;
        padding-bottom: 120px;
    `)}
`